<template>
  <div class="entity col-xl-5 mx-auto">
    <b-form-group 
    label="Copy Existing Entity" 
    v-if="entitiesForChange.length">
      <b-form-select 
      :value="0"
      :options="entitiesForChange" 
      @change="copyExistingChanged"
      text-field="entityName" 
      value-field="entityID"
      :disabled="isViewOnly">
          <template slot="first">
              <option :value="0" disabled>-- Select an existing entity to copy (optional) --</option>
          </template>
      </b-form-select>
    </b-form-group>
    <b-form-group>
      <b-form-row>
        <b-form-group label="Type of Entity" class="col-8" :state="IsFieldValid($v.entity.entityTypeID)" :invalid-feedback="RequiredMessage('Entity Type')">
            <b-form-select 
              v-model="$v.entity.entityTypeID.$model" 
              type="text" 
              :options="entityTypes" 
              :state="IsFieldValid($v.entity.entityTypeID)" 
              text-field="description" 
              value-field="entityTypeID"
              :disabled="isViewOnly">
                <template slot="first">
                    <option :value="0" disabled>-- Type of Entity --</option>
                </template>
            </b-form-select>
        </b-form-group>
        <b-form-group 
          label="New or Existing Entity" 
          class="col-4" 
          :state="IsFieldValid($v.entity.isNew)" 
          :invalid-feedback="RequiredMessage('New or Existing Entity')"
          v-b-tooltip.hover.v-secondary title="The entity exists as a business in NJ or the entity is a completely new business.">
          <b-form-radio-group 
            :options="[{text: 'New', value: true}, {text: 'Existing', value: false}]" 
            v-model="$v.entity.isNew.$model" 
            :state="IsFieldValid($v.entity.isNew)"
            :disabled="isViewOnly"></b-form-radio-group>
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Entity Name" class="col-8" :state="IsFieldValid($v.entity.entityName)" :invalid-feedback="RequiredAndMaxLengthMessage('Entity Name', $v.entity.entityName)">
          <b-form-input v-model="$v.entity.entityName.$model" :state="IsFieldValid($v.entity.entityName)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Entity NJTIN" class="col-4" :state="IsFieldValid($v.entity.fein)" :invalid-feedback="njtinErrorMessage('NJTIN', $v.entity.fein)">
          <b-form-input v-model="$v.entity.fein.$model" :state="IsFieldValid($v.entity.fein)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Address 1" class="col-6" :state="IsFieldValid($v.entity.address.address1)" :invalid-feedback="RequiredAndMaxLengthMessage('Address 1', $v.entity.address.address1)">
          <b-form-input v-model="$v.entity.address.address1.$model" :state="IsFieldValid($v.entity.address.address1)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Address 2" class="col-6" :state="IsFieldValid($v.entity.address.address2)" :invalid-feedback="MaxLengthMessage('Address 2', $v.entity.address.address2.$params.maxLength.max)">
          <b-form-input v-model="$v.entity.address.address2.$model" :state="IsFieldValid($v.entity.address.address2)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-checkbox v-model="entity.address.foreignAddress" id="foreign-address" name="foreign-address" class="text-right" :disabled="isViewOnly">
      Foreign Address?
    </b-form-checkbox>

    <b-form-group>
      <b-form-row>
        <b-form-group label="City" :class="{ 'col-6': isForeign, 'col-4': !isForeign }"  :state="IsFieldValid($v.entity.address.city)" :invalid-feedback="RequiredAndMaxLengthMessage('City', $v.entity.address.city)">
          <b-form-input v-model="$v.entity.address.city.$model" :state="IsFieldValid($v.entity.address.city)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="State" :class="{ 'd-none': isForeign, 'col-4': !isForeign }" :state="IsFieldValid($v.entity.address.state)" :invalid-feedback="RequiredAndMaxLengthMessage('State', $v.entity.address.state)">
          <b-form-input v-model="$v.entity.address.state.$model" :state="IsFieldValid($v.entity.address.state)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Zip" :class="{ 'col-6': isForeign, 'col-4': !isForeign }" :state="IsFieldValid($v.entity.address.zip)" :invalid-feedback="zipCodeMessage('Zip')">
          <b-form-input v-model="$v.entity.address.zip.$model" :state="IsFieldValid($v.entity.address.zip)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group v-if="isForeign">
      <b-form-row>
        <b-form-group label="Area" class="col-6" :state="IsFieldValid($v.entity.address.area)" :invalid-feedback="RequiredAndMaxLengthMessage('Area', $v.entity.address.area)">
          <b-form-input v-model="$v.entity.address.area.$model" :state="IsFieldValid($v.entity.address.area)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Country" class="col-6" :state="IsFieldValid($v.entity.address.country)" :invalid-feedback="RequiredAndMaxLengthMessage('Country', $v.entity.address.country)">
          <b-form-input v-model="$v.entity.address.country.$model" :state="IsFieldValid($v.entity.address.country)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group label="Website" :state="IsFieldValid($v.entity.websiteURL)" :invalid-feedback="MaxLengthMessage('Website', $v.entity.websiteURL.$params.maxLength.max)">
      <b-form-input v-model="$v.entity.websiteURL.$model" :state="IsFieldValid($v.entity.websiteURL)" :disabled="isViewOnly" />
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Contact Name" class="col-4" :state="IsFieldValid($v.entity.contactName)" :invalid-feedback="RequiredAndMaxLengthMessage('Contact Name', $v.entity.contactName)">
          <b-form-input v-model="$v.entity.contactName.$model" :state="IsFieldValid($v.entity.contactName)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Contact Email" class="col-4" :state="IsFieldValid($v.entity.contactEmail)" :invalid-feedback="EmailMessage('Contact Email', $v.entity.contactEmail)">
          <b-form-input v-model="$v.entity.contactEmail.$model" :state="IsFieldValid($v.entity.contactEmail)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Contact Phone" class="col-4" :state="IsFieldValid($v.entity.phone)" :invalid-feedback="RequiredAndPhoneMessage($v.entity.phone)">
          <b-form-input v-model="$v.entity.phone.$model" :state="IsFieldValid($v.entity.phone)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Carrier Name" class="col-6" :state="IsFieldValid($v.entity.carrierName)" :invalid-feedback="MaxLengthMessage('Carrier Name', $v.entity.carrierName.$params.maxLength.max)">
          <b-form-input v-model="$v.entity.carrierName.$model" :state="IsFieldValid($v.entity.carrierName)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Policy Number" class="col-6" :state="IsFieldValid($v.entity.policyNumber)" :invalid-feedback="MaxLengthAndAlphaNumMessage('Policy Number', $v.entity.policyNumber)">
          <b-form-input v-model="$v.entity.policyNumber.$model" :state="IsFieldValid($v.entity.policyNumber)" :disabled="isViewOnly" />
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Coverage ID" class="col-6" :state="IsFieldValid($v.entity.coverageId)" :invalid-feedback="MaxLengthMessage('Coverage ID', $v.entity.coverageId.$params.maxLength.max)">
          <b-form-input v-model="$v.entity.coverageId.$model" :state="IsFieldValid($v.entity.coverageId)" :disabled="isViewOnly" />
        </b-form-group>
        <b-form-group label="Coverage Effective Date" class="col-6">
          <datepicker :value="entity.effectiveDate ? moment(entity.effectiveDate).toDate() : ''" 
            @selected="entity.effectiveDate = $event ? moment($event).format('YYYY-MM-DD') : ''" 
            input-class="form-control" 
            calendar-class="calendar-top"
            wrapper-class="wrapper-top"
            format="MM/dd/yyyy" 
            typeable
            :disabled="isViewOnly"></datepicker>
        </b-form-group>
      </b-form-row>
    </b-form-group>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { required, requiredIf, minLength, maxLength, minValue, numeric, email, alphaNum } from 'vuelidate/lib/validators'
import CustomValidations from '../mixins/customValidations.js'
const phoneValidation = CustomValidations.phoneValidation

const zipCode = function(zip) {
  return this.isForeign || false ? zip.match(/^[a-z0-9]{0,10}$/gi) ? true : false : zip.match(/^[0-9]{5}$/) ? true : false
}

export default {
  name: 'Entity',
  props: {
    entityProp: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      entity: this.entityProp,
      dirty: false
    }
  },
  validations: {
    entity: {
      entityTypeID: {
        minValue: minValue(1)
      },
      isNew: {
        required
      },
      entityName: {
        required,
        maxLength: maxLength(250)
      },
      fein: {
        maxLength: maxLength(12),
        minLength: minLength(12),
        numeric
      },
      address: {
        address1: {
          required,
          maxLength: maxLength(50)
        },
        address2: {
          maxLength: maxLength(50)
        },
        city: {
          required,
          maxLength: maxLength(50)
        },
        state: {
          required: requiredIf(function() { return !this.isForeign }),
          maxLength: maxLength(2)
        },
        zip: {
          required: requiredIf(function() { return !this.isForeign }),
          maxLength: maxLength(10),
          zipCode
        },
        country: {
          required: requiredIf(function() { return this.isForeign }),
          maxLength: maxLength(50)
        },
        area: {
          required: requiredIf(function() { return this.isForeign }),
          maxLength: maxLength(25)
        }        
      },
      contactName: {
        required,
        maxLength: maxLength(30)
      },
      phone: {
        required,
        phoneValidation
      },
      contactEmail: {
        required,
        maxLength: maxLength(50),
        email
      },
      websiteURL: {
        maxLength: maxLength(50)
      },
      carrierName: {
        maxLength: maxLength(50)
      },
      policyNumber: {
        maxLength: maxLength(50),
        alphaNum
      },
      coverageId: {
        maxLength: maxLength(20)
      }
    }
  },
  methods: {
    copyExistingChanged(entityId) {
      const entity = this.$store.state.entity.entities.find(e => e.entityID == entityId)
      if (!entity) return
      
      this.$v.entity.entityTypeID.$model = entity.entityTypeID
      this.$v.entity.isNew.$model = entity.isNew
      this.$v.entity.entityName.$model = entity.entityName
      this.$v.entity.fein.$model = entity.fein
      this.$v.entity.address.address1.$model = entity.address.address1
      this.$v.entity.address.address2.$model = entity.address.address2
      this.$v.entity.address.city.$model = entity.address.city
      this.$v.entity.address.state.$model = entity.address.state
      this.$v.entity.address.zip.$model = entity.address.zip
      this.$v.entity.address.country.$model = entity.address.country
      this.$v.entity.address.area.$model = entity.address.area
      this.entity.address.foreignAddress = entity.address.foreignAddress
      this.$v.entity.contactName.$model = entity.contactName
      this.$v.entity.contactEmail.$model = entity.contactEmail
      this.$v.entity.phone.$model = entity.phone
      this.$v.entity.websiteURL.$model = entity.websiteURL
      this.$v.entity.carrierName.$model = entity.carrierName
      this.$v.entity.policyNumber.$model = entity.policyNumber
      this.$v.entity.coverageId.$model = entity.coverageId
      this.entity.effectiveDate = entity.effectiveDate
    },
    njtinErrorMessage(name, field) {
      return !field.numeric ? `${name} must be numeric.` : `${name} must be ${field.$params.maxLength.max} digits.`
    },
    zipCodeMessage(name) {
      const field = this.$v.entity.address.zip
      return !field.required ? this.RequiredMessage(name) : this.isForeign ? this.MaxLengthMessage(name, field.$params.maxLength.max) : `${name} must be 5 digits.` 
    },
    nextClicked() {
      if (this.isViewOnly) {
        this.$router.push({
          name: 'officer/list',
          params: {
            submissionId: this.submission.submissionID,
            changeId: this.entity.changeID,
            entityId: this.entity.entityID
          }
        })
      } else {
        this.$v.$touch()
        if (this.$v.$invalid) return

        this.$store.dispatch('entity/save', this.entity).then(() => {
          this.dirty = false
          const e = this.$store.state.entity.entity
          this.$router.push({
            name: 'officer/list',
            params: {
              submissionId: this.submission.submissionID,
              changeId: e.changeID,
              entityId: e.entityID
            }
          })
        })
      }
    },
    backClicked() {
      this.$router.push({
        name: 'entity/list',
        params: {
          submissionId: this.submission.submissionID,
          changeId: this.entity.changeID
        }
      })
    }
  },
  computed: {
    entitiesForChange() {
      return this.$store.state.entity.entities.filter(e => e.changeID == this.entity.changeID)
    },
    isForeign() {
      return this.entity.address.foreignAddress == true
    },
    ...mapState({
      submission: state => state.submission.submission,
      changeTypes: state => state.application.changeTypes,
      entityTypes: state => state.application.entityTypes
    }),
    ...mapGetters({
      isViewOnly: 'application/isChangeViewOnly'
    })
  },
  watch: {
    entity: {
      handler() {
        this.dirty = true
      },
      deep: true
    }
  },
  created() {
    this.$EventBus.$on('back-clicked', this.backClicked)
    this.$EventBus.$on('next-clicked', this.nextClicked)
  },
  beforeDestroy() {
    this.$EventBus.$off('back-clicked', this.backClicked)
    this.$EventBus.$off('next-clicked', this.nextClicked)
  },  
  beforeRouteLeave(to, from, next) {
    if (!this.dirty) return next()
    
    this.ConfirmLeave().then(answer => {
      if (answer == this.ConfirmLeaveEventTypes.Discard) return next()
      else if (answer == this.ConfirmLeaveEventTypes.Save) {
        this.$v.$touch()
        if (this.$v.$invalid) return next(false)
        else return this.$store.dispatch('entity/save', this.entity).then(() => next())
      }
      else return next(false)
    })
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1.5rem !important;
}

.form-row .form-group {
  margin-bottom: 0 !important;
}
</style>